import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/platform/AppShell";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";
import { Panel } from "@/components/rights/RightsBadges";
import { ReviewBadge } from "@/components/vendors/VendorBadges";
import { QuestionRow } from "@/components/vendors/QuestionRow";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtD } from "@/lib/platform";
import { scoreTone } from "@/lib/vendors";

const DomainStrip = ({ scoring }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="domain-scores">
    {Object.entries(scoring.domains).map(([k, d]) => (
      <a key={k} href={`#domain-${k}`} className="bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-[#003366] transition-colors" data-testid={`domain-score-${k}`}>
        <div className="text-[11px] font-semibold text-[#003366] truncate">{d.label}</div>
        <div className="flex items-baseline justify-between">
          <span className={`text-lg font-mono font-bold ${scoreTone(d.score)}`}>{d.score == null ? "—" : `${d.score}%`}</span>
          <span className="text-[10px] font-mono text-slate-400">{d.answered}/{d.total}</span>
        </div>
      </a>
    ))}
  </div>
);

export default function VendorAssessment() {
  const { vid, aid } = useParams();
  const auth = useAuth();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setA((await api.get(`/vendors/${vid}/assessments/${aid}`)).data); } catch (e) { setErr(apiError(e, "Could not load assessment")); }
  }, [vid, aid]);
  useEffect(() => { load(); }, [load]);

  if (err) return <AppShell testId="vendor-assessment-page"><div className="text-sm text-red-600">{err}</div></AppShell>;
  if (!a) return <AppShell testId="vendor-assessment-page"><div className="h-40 rounded-xl bg-slate-100 animate-pulse" /></AppShell>;
  const editable = a.editable && auth.can("vendors", "edit");
  const s = a.scoring;
  const noComment = a.questions.filter((q) => ["NO", "PARTIAL"].includes(q.response?.answer) && !(q.response?.comment || "").trim()).length;
  const blockers = [s.total - s.answered > 0 && `${s.total - s.answered} unanswered`, noComment > 0 && `${noComment} gap(s) need a comment`].filter(Boolean);
  const domains = [...new Set(a.questions.map((q) => q.domain))];

  const submit = async () => {
    setBusy(true);
    try { await api.post(`/vendors/${vid}/assessments/${aid}/submit`, {}); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(apiError(e, "Could not submit")); } finally { setBusy(false); }
  };

  return (
    <AppShell testId="vendor-assessment-page">
      <Link to={`/vendors/${vid}?tab=assessments`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#003366]" data-testid="assessment-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> {a.vendor.name}
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-4 rr-rise">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-500" data-testid="assessment-ref">{a.reference_number}</span>
            <ReviewBadge status={a.status} label={a.status_label} testId="assessment-status" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-[#003366] mt-1.5">{a.cycle} vendor assessment</h1>
          <p className="text-sm text-slate-600 mt-1">{a.vendor.name} · due {fmtD(a.due_date)} · {s.gaps} gap(s)</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Assessment score</div>
            <div className={`text-3xl font-mono font-bold ${scoreTone(s.overall)}`} data-testid="assessment-overall">{s.overall == null ? "—" : `${s.overall}%`}</div>
          </div>
          {editable && (
            <div className="text-right">
              <Button onClick={submit} disabled={busy || blockers.length > 0} className="bg-[#003366] hover:bg-[#003366]/90" data-testid="assessment-submit-button">
                <Send className="w-4 h-4 mr-1" /> Submit for approval</Button>
              {blockers.length > 0 && <div className="text-[11px] text-amber-700 mt-1" data-testid="assessment-blockers">{blockers.join(" · ")}</div>}
            </div>
          )}
        </div>
      </div>
      <DomainStrip scoring={s} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-4">
          {domains.map((d) => {
            const qs = a.questions.filter((q) => q.domain === d);
            return (
              <Panel key={d} eyebrow={`Domain · ${s.domains[d].answered}/${s.domains[d].total} answered`} title={qs[0].domain_label} testId={`domain-${d}`}>
                <div id={`domain-${d}`} className="divide-y divide-slate-100 -my-3">
                  {qs.map((q) => <QuestionRow key={`${q.key}-${q.response?.answer}`} a={a} q={q} editable={editable} onSaved={setA} onChanged={load} />)}
                </div>
              </Panel>
            );
          })}
        </div>
        <aside className="xl:col-span-4 space-y-6">
          <Panel eyebrow="Approval engine" title="Approval" testId="assessment-approval">
            {a.approvals.length === 0 ? <div className="text-xs text-slate-500">Submit the completed questionnaire to request approval. Another approver must decide.</div>
              : <ApprovalPanel approval={a.approvals[0]} currentUser={auth.user} onChanged={load} />}
          </Panel>
          <Panel eyebrow="Scoring" title="How the score works" testId="assessment-scoring-help">
            <ul className="text-xs text-slate-600 space-y-1">
              <li>Yes = 100%, Partial = 50%, No = 0%. N/A is excluded.</li>
              <li>Each domain is the average of its questions. The overall score is the average of the domains.</li>
              <li>Approval feeds the score into residual risk. Inherent risk does not change.</li>
            </ul>
          </Panel>
        </aside>
      </div>
    </AppShell>
  );
}
